import { HttpParams } from '@angular/common/http'
import { Component } from '@angular/core'
import { FormsModule } from '@angular/forms'
import { faSearch } from '@fortawesome/free-solid-svg-icons'
import { WjGridModule } from '@grapecity/wijmo.angular2.grid'
import { WjGridFilterModule } from '@grapecity/wijmo.angular2.grid.filter'
import { WjInputModule } from '@grapecity/wijmo.angular2.input'
import { CellEditEndingEventArgs, CellRangeEventArgs, CellType, Column, DataMap, FlexGrid, GridPanel } from '@grapecity/wijmo.grid'

import { BaseComponent, Bid, FeatureType, Grid, ModalBackDrop, ModalSize, VpUser } from '../../shared'
import { ActionToolbarButtonsComponent } from '../../shared/reusable-components/action-toolbar-buttons/action-toolbar-buttons.component'
import { ActionToolbarCount } from '../../shared/reusable-components/action-toolbar-count/action-toolbar-count.component'
import { ActionToolbarSearchComponent } from '../../shared/reusable-components/action-toolbar-search/action-toolbar-search.component'
import { GridExportComponent } from '../../shared/reusable-components/grid-export/grid-export.component'
import { GridSelectionCalculationComponent } from '../../shared/reusable-components/grid-selection-calculation/grid-selection-calculation.component'
import { AutoSizeDirective } from '../../shared/utility/auto-size.directive'
import { BidComponent } from './bid.component'
import { CompetitorBidsComponent } from './competitor-bids.component'

@Component({
    templateUrl: './histories.component.html',
    standalone: true,
    imports: [
        FormsModule,
        ActionToolbarCount,
        ActionToolbarButtonsComponent,
        ActionToolbarSearchComponent,
        GridExportComponent,
        WjGridModule,
        AutoSizeDirective,
        WjGridFilterModule,
        GridSelectionCalculationComponent,
        WjInputModule,
    ],
})
export class HistoriesComponent extends BaseComponent {
    faSearch = faSearch
    active = 1
    grid: Grid<Bid>
    allItems: Bid[] = []
    showAll = false
    jobTypeMap!: DataMap
    countiesMap!: DataMap
    ownersMap!: DataMap
    allUserMap!: DataMap
    activeEstimatorUserMap!: DataMap
    biddersMap!: DataMap
    rangeMap!: DataMap

    get canEdit() {
        return this.services.authentication.hasFeature([FeatureType.BidAnalyticsEdit])
    }

    constructor() {
        super()
        this.grid = new Grid<any>(this.services, 'id', this)
            .wrapColumnHeaders()
            .filterBy(item => [item.heavyBidNum, item.estimator, item.owner, item.job, item.bidType])
            .setupModalEditing(this.services.data.bid, 'Bid', BidComponent, () => new Bid(), this, ModalSize.medium)
            .isReadOnly(!this.canEdit)
            .editOnDoubleClick(false)

        this.refresh()
    }

    refresh() {
        this.cancelableRequest(
            '',
            true,
            this.services.data
                .load(
                    true,
                    true,
                    this.services.data.bid.queryAll(),
                    this.services.data.jobTypes.queryAll(),
                    this.services.data.counties.queryAll(),
                    this.services.data.get('/api/VpOwner/GetOwners'),
                    this.services.data.get('/api/VpOwner/GetCompetitors'),
                    this.services.data.get('api/VpUser/GetVistaUsers', new HttpParams().set('activeOnly', false)),
                    this.services.data.get('api/VpUser/GetVistaEstimatorUsers'),
                    this.services.data.bidRanges.queryAll(),
                )
                .subscribe({
                    next: result => {
                        this.initialLoadingComplete()
                        this.allItems = result[0]
                        this.jobTypeMap = new DataMap(result[1], 'id', 'fullName')
                        this.countiesMap = new DataMap(result[2], 'id', 'combinedName')
                        this.ownersMap = new DataMap(result[3], 'id', 'name')
                        this.biddersMap = new DataMap(result[4], 'id', 'name')
                        this.rangeMap = new DataMap(result[7], 'id', 'name')

                        // We want the grid to show a value for any user, even inactive ones.
                        this.allUserMap = new DataMap(result[5] as VpUser[], 'id', 'name')

                        // When the grid is edited, we will only show active users as options.
                        this.activeEstimatorUserMap = new DataMap(result[6] as VpUser[], 'id', 'name')

                        this.filterItems()
                    },
                }),
        )
    }

    filterItems() {
        this.grid.items = this.showAll ? this.allItems : this.allItems.filter(x => x.wasBid)
    }

    mainGridItemFormatter(panel: GridPanel, r, c, cell: HTMLDivElement) {
        if (panel.cellType === CellType.Cell) {
            const row = panel.grid.rows[r]
            const rowItem = row.dataItem

            cell.classList.remove('green-cell')

            // Green when VanCon is marked as won in competitor bids (not derived rank)
            if (rowItem.competitorBids?.some(cb => cb.competitorId === -1 && cb.won === true)) {
                cell.classList.add('green-cell')
            }
        }
    }

    cellBeginningEdit(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column data map to just active users, so inactive users cannot be selected.
        const col = flexGrid.columns[args.col] as Column
        if (col.binding == 'estimatorHrRef') col.dataMap = this.activeEstimatorUserMap
    }

    cellEditEnded(flexGrid: FlexGrid, args: CellEditEndingEventArgs) {
        // Set the column mapping back to all users, so inactive user names still appear.
        const col = flexGrid.columns[args.col]
        if (col.binding == 'estimatorHrRef') col.dataMap = this.allUserMap

        if (args.cancel) return
        this.updateBids([flexGrid.rows[args.row].dataItem])
    }

    pasting(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const rng = args.range
        const itemIds: string[] = []
        let row = Math.min(rng.row, rng.row2)
        const row2 = Math.max(rng.row, rng.row2)
        for (row; row <= row2; row++) {
            const dataItem = flexGrid.rows[row].dataItem
            itemIds.push(dataItem.id)
        }

        // Since the grid will sort and filter the pasted cells before the 'onCellPasted' even fires,
        // we need to keep a record of the items we are changing, so they wrong items don't get send to the server.
        flexGrid['controller'].itemIds = itemIds
    }

    pasted(flexGrid: FlexGrid, args: CellRangeEventArgs) {
        if (args.cancel) return
        const itemsToSave = flexGrid.collectionView.items.filter(x => flexGrid['controller'].itemIds.indexOf(x.id) > -1)
        this.updateBids(itemsToSave)
        flexGrid['controller'].pastedRimnumbers = [] // clear immediately and not on promise fulfillment.
    }

    updateBids(items: Bid[]) {
        this.services.data.put('api/Bid/BulkUpdate', items).subscribe({
            error: () => {
                this.services.notification.error('The bid changes were not successfully saved')
            },
        })
    }

    openCompetitorBidsModal(bid: Bid) {
        const ref = this.services.modals.open(CompetitorBidsComponent, ModalSize.medium, ModalBackDrop.darkAndActive)
        const instance = ref.componentInstance as CompetitorBidsComponent
        instance.bid = this.services.utility.copy(bid)
        instance.biddersMap = this.biddersMap

        // Add a placeholder for a 'Select a bidder' option, but only if it has not already been added.
        if (instance.biddersMap.collectionView.items.find(item => item.id === 0) === undefined) {
            instance.biddersMap.collectionView.items.unshift({ id: 0, name: 'Select a Bidder' })
        }

        ref.result.then(
            () => {
                // Refresh the grid to show updated data
                this.refresh()
            },
            () => {
                // User cancelled, do nothing
            },
        )
    }

    getBiddersCount(bid: Bid): string {
        if (bid.competitorBids && bid.competitorBids.length > 0) {
            return `${bid.competitorBids.length} ${bid.competitorBids.length === 1 ? 'Bid' : 'Bids'}`
        }
        return 'No Bids'
    }

    onCheckboxChange(event: Event): void {
        event.preventDefault()
    }

    openAbstract(url: string) {
        window.open('./Resources/Images/BidAbstract/' + url, '_blank')
    }
}
